// src-lit/components/product/kd-product-delivery.ts
import { LitElement, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { onEvent } from '../../utils/dom';
import { EVENT_NAMES, type VariantSelectedDetail } from '../../constants/events';
import { SELECTORS } from '../../constants/selectors';

interface DeliveryVariantEntry {
  /** クール便送料テキスト（variant.weight 由来、空文字なら未設定） */
  coolShippingFeeText: string;
  /** 出荷元ラベル（'出品者直送' / 'クラダシから出荷' / ''） */
  warehouseLabel: string;
  /** 配送業者名（空文字なら非表示） */
  deliveryCompany: string;
}

/**
 * @element kd-product-delivery
 * @description variant 切替に応じて配送テーブルを更新する。
 *   - クール便送料 td: textContent（weight 由来）
 *   - 出荷元 tr: hidden + 内 td の textContent（warehouse_name metafield 由来）
 *   - 配送業者 tr: hidden + 内 td の textContent（delivery_company_name metafield 由来）
 *   送料 / 配送温度帯 / 配送可能地域 は product 単位なので variant 不変。
 */
@customElement('kd-product-delivery')
export class KdProductDelivery extends LitElement {
  createRenderRoot() {
    return this;
  }

  private unsubscribe: (() => void) | null = null;

  private variantMap: Record<string, DeliveryVariantEntry | undefined> = {};

  render() {
    return nothing;
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.loadVariantMap();
    this.unsubscribe = onEvent<VariantSelectedDetail>(
      document,
      EVENT_NAMES.PRODUCT.VARIANT_SELECTED,
      (e) => this.handleVariantSelected(e.detail),
    );
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.unsubscribe?.();
    this.unsubscribe = null;
  }

  private loadVariantMap(): void {
    const script = this.querySelector<HTMLScriptElement>(
      SELECTORS.PRODUCT_DELIVERY.VARIANT_MAP,
    );
    if (!script?.textContent) return;
    try {
      this.variantMap = JSON.parse(script.textContent);
    } catch {
      // JSON parse 失敗時は空マップのまま
    }
  }

  private handleVariantSelected(detail: VariantSelectedDetail): void {
    const entry = this.variantMap[detail.variantId];
    if (!entry) return;

    const coolCell = this.querySelector<HTMLElement>(
      SELECTORS.PRODUCT_DELIVERY.COOL_SHIPPING_FEE,
    );
    if (coolCell) coolCell.textContent = entry.coolShippingFeeText;

    this.updateRow(
      SELECTORS.PRODUCT_DELIVERY.WAREHOUSE_ROW,
      SELECTORS.PRODUCT_DELIVERY.WAREHOUSE_LABEL,
      entry.warehouseLabel,
    );
    this.updateRow(
      SELECTORS.PRODUCT_DELIVERY.DELIVERY_COMPANY_ROW,
      SELECTORS.PRODUCT_DELIVERY.DELIVERY_COMPANY,
      entry.deliveryCompany,
    );
  }

  private updateRow(
    rowSelector: string,
    labelSelector: string,
    value: string,
  ): void {
    const row = this.querySelector<HTMLElement>(rowSelector);
    if (!row) return;
    if (value === '') {
      row.hidden = true;
      return;
    }
    row.hidden = false;
    const label = row.querySelector<HTMLElement>(labelSelector);
    if (label) label.textContent = value;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'kd-product-delivery': KdProductDelivery;
  }
}
